<template>
    <div class="card__container flex-1">
        <span class="card__caption">{{ getCardCaption }}</span>
        <div class="card__wrapper">
            <div class="card__header">
                <div class="card__title">{{ getTitle }}</div>
                <div class="card__description">
                    {{ getCardDescription }}
                </div>
            </div>
            <div class="card__footer">
                <div class="card__price_container">
                    <template v-if="activeRatio > 1 || additionalFactor < 1">
                        <div>
                            <span class="card__price_base">{{ getPriceFormatted }}</span>
                            <span class="card__price_real">{{ getRealPriceFormatted }}</span>
                            <span class="card__price_ratio">{{ getPriceRatio }}</span>
                        </div>
                        <span class="card__price_saved" v-html="getSavingFormatted" />
                    </template>
                    <template v-else>
                        <div>
                            <span class="card__price_real">{{ getPriceFormatted }}</span>
                            <span class="card__price_ratio">{{ getPriceRatio }}</span>
                        </div>
                    </template>
                </div>
                <div v-if="isPaymentForm" class="card__button_container">
                    <form :action="getPaymentAction" method="post">
                        <input type="hidden" name="act" value="prolong" />
                        <input type="hidden" name="srok" :value="getSrok" />
                        <input type="hidden" name="tarifprolong" :value="template.id" />
                        <input type="submit" :value="getPaymentButtonText">
                    </form>
                </div>
                <div v-else class="card__button_container" v-html="getButton"></div>
                <div class="card__about">
                    <div></div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.recordsPerReport") }}</span>
                        <span>{{ isUnlimit(template.recordsPerReport) ? $t("lang.limitModal.unlimit") : formatNumber(template.recordsPerReport) }}</span>
                    </div>
                    <div>
                        <span>{{ $tm("lang.pages.tariff.card.analyzePerDay") }}</span>
                        <span>{{ formatNumber(template.analyzePerDay) }}</span>
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.monitoring") }}</span>
                        <img :src="this.getIconsUrl(true)" />
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.aiTracker") }}</span>
                        <img :src="this.getIconsUrl(true)" />
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.projectsLimit") }}</span>
                        <span>{{ formatNumber(template.projectsLimit) }}</span>
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.sitesNumberToCompare") }}</span>
                        <span>{{ template.sitesNumberToCompare }}</span>
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.keysPerGroupReport") }}</span>
                        <span>{{ isUnlimit(template.keysPerGroupReport) ? $t("lang.limitModal.unlimit") : formatNumber(template.keysPerGroupReport) }}</span>
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.searchKeys") }}</span>
                        <span>{{ getSearchKeys }}</span>
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.rsyaPerDay") }}</span>
                        <span>{{ formatNumber(template.rsyaPerDay) }}</span>
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.rsyaExportRowsLimit") }}</span>
                        <span>{{ isUnlimit(template.rsyaExportRowsLimit) ? $t("lang.limitModal.unlimit") : formatNumber(template.rsyaExportRowsLimit) }}</span>
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.usersNumber") }}</span>
                        <span>{{ template.usersNumber }}</span>
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.analyzeLinks") }}</span>
                        <img :src="getAnalyzeLinks" />
                    </div>
                    <div>
                        <span v-if="isPremiumApi">{{ $t("lang.pages.tariff.card.apiPremium") }}</span>
                        <span v-else>{{ $t("lang.pages.tariff.card.api") }}</span>
                        <img :src="getApiAvailable" />
                    </div>
                    <div>
                        <span>{{ $t("lang.pages.tariff.card.limits") }}</span>
                        <a v-if="isCanExpandedTariff" class="font-bold" href="#" @click.prevent="onOpenVerbox">{{ $t("lang.pages.tariff.card.limits_expanded") }}</a>
                        <span v-else class="font-bold">{{ $t("lang.pages.tariff.card.limits_fixed") }}</span>
                    </div>
                </div>
                <div class="card__more">
                    <a href="#" @click.prevent="scrollToDetails">{{ $t("lang.pages.tariff.card.details") }}</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import animateScrollTo from "animated-scroll-to";
import {FormatHelper} from "keysso/helpers/FormatHelper";
import {TARIFF_BASIC, TARIFF_PROFESSIONAL, TARIFF_CORPORATE} from 'keysso/modules/Calculator';
import Verbox from "keysso/mixins/Verbox";
import Limits from "keysso/mixins/Limits";

export default {
    name: "TariffPageCard",
    mixins: [Verbox, Limits],
    props: {
        activeRatio: Number,
        activePercent: Number,
        template: {
            id: Number,
            price: Number,
            recordsPerReport: Number,
            analyzePerDay: Number,
            sitesNumberToCompare: Number,
            keysPerGroupReport: Number,
            searchKeysPerDay: Number,
            searchKeysPerMonth: Number,
            usersNumber: Number,
            projectsLimit: Number,
            rsyaPerDay: Number,
            rsyaExportRowsLimit: Number,
        },
        user: {
            _id: Number,
            parent: Number,
            isPayed: Boolean,
            isHigher: Boolean
        },
        userTariffId: Number,
        isDemo: Boolean,
        locale: String,
        detailsTitleId: String,
        termRelations: Object,
    },
    data() {
        return {
            templateRelations: {
                [TARIFF_BASIC]: {
                    color: 'linear-gradient(90deg, #159D03 0%, #6AA300 46.15%, #C9D705 100%)',
                    caption: this.$t('lang.pages.tariff.card.basic_caption'),
                },
                [TARIFF_PROFESSIONAL]: {
                    color: 'linear-gradient(90deg, #011110 4.81%, #063735 59.52%, #2C4F7D 100%)',
                    caption: this.$t('lang.pages.tariff.card.professional_caption'),
                },
                [TARIFF_CORPORATE]: {
                    color: 'linear-gradient(90deg, #0063E4 0%, #9900FF 100%)',
                    caption: this.$t('lang.pages.tariff.card.corporate_caption'),
                }
            }
        }
    },
    computed: {
        getTitle() {
            return this.template.label;
        },
        getPriceFormatted() {
            return FormatHelper.number(this.template.price, '₽');
        },
        getPriceRatio() {
            return this.$t("lang.pages.tariff.card.perMonth");
        },
        getRealPrice() {
            return (this.template.price * this.activeRatio * (1 - (this.activePercent / 100)) * this.additionalFactor / this.activeRatio).toFixed();
        },
        additionalFactor() {
            // #promotion
            let date = new Date()
            if ((new Date('2026-05-28T00:00:00+03:00')) < date && date < (new Date('2026-06-02T00:00:00+03:00'))) {
                return 0.8
            }
            return 1
        },
        getRealPriceFormatted() {
            return FormatHelper.number(
                this.getRealPrice,
                '₽'
            );
        },
        getSavingFormatted() {
            return this.$t(
                "lang.pages.tariff.card.saved",
                [FormatHelper.number((this.template.price - this.getRealPrice) * this.activeRatio, '₽')]
            );
        },
        getSearchKeys() {
            return FormatHelper.number(this.template.searchKeysPerDay) + '/' + FormatHelper.number(this.template.searchKeysPerMonth)
        },
        getCardDescription() {
            return this.$t("lang.pages.tariff.card.description.id_" + this.template.id);
        },
        getButton() {
            // гостям показываем регистрацию со ссылкой на выбранный тариф
            if (this.user.id === 0) {
                return sprintf(
                    '<a href="/%s/registration">%s</a>',
                    this.locale,
                    this.$t("lang.pages.tariff.card.button.register")
                );
            }

            // если не владелец тарифа
            if (this.user.parent !== 0) {
                return sprintf(
                    '<span title="%s">%s</span>',
                    this.$t("lang.pages.tariff.card.button.parent"),
                    this.$t("lang.pages.tariff.card.button.change")
                );
            }

            // если более высокий тариф - показываем "сменить"
            if (this.template.isHigher) {
                return sprintf(
                    '<a href="/%s/panel#changePlanPanel">%s</a>',
                    this.locale,
                    this.$t("lang.pages.tariff.card.button.change")
                );
            }

            // для всех остальных случаев не активная кнопка "сменить"
            return sprintf(
                '<span title="%s">%s</span>',
                this.$t("lang.pages.tariff.card.button.later"),
                this.$t("lang.pages.tariff.card.button.change")
            );
        },
        getPaymentAction() {
            return '/' + this.locale + '/panel';
        },
        getSrok() {
            return Object.keys(this.termRelations).find(key => this.termRelations[key] === this.activeRatio);
        },
        getPaymentButtonText() {
            return this.user.isPayed && !this.isDemo && this.user.tariff.number == this.user.tariff.realNumber
                ? this.$t("lang.pages.tariff.card.button.prolong")
                : this.$t("lang.pages.tariff.card.button.choose");
        },
        /**
         * форма оплаты для авторизованных пользователей, не для промо тарифа, только для владельца тарифа и
         * если данный тариф уже оплачен у пользователя или закончен его срок
         */
        isPaymentForm() {
            return this.user.id !== 0
                && this.user.parent === 0
                && (this.userTariffId === this.template.id || !this.user.isPayed || (this.user.tariff.number !== this.user.tariff.realNumber));
        },
        getAnalyzeLinks() {
            return this.getIconsUrl(this.template.isLinksAvailable);
        },
        getApiAvailable() {
            return this.getIconsUrl(this.template.isApiAvailable);
        },
        isCanExpandedTariff() {
            return this.template.id === TARIFF_CORPORATE;
        },
        isPremiumApi() {
            return this.template.id === TARIFF_CORPORATE;
        },
        getCardBackgroundColor() {
            return this.templateRelations[this.template.id].color;
        },
        getCardCaption() {
            return this.templateRelations[this.template.id].caption;
        }
    },
    methods: {
        formatNumber(number) {
            return FormatHelper.number(number);
        },
        getIconsUrl(value) {
            return sprintf('/i/icons/%s.svg', value ? 'yes' : 'no');
        },
        scrollToDetails() {
            animateScrollTo(document.getElementById(this.detailsTitleId));
        }
    }
}
</script>

<style scoped>
.card__container {
    display: flex;
    flex-direction: column;
    border-radius: 36px;
    min-width: 335px;
    margin-top: 25px;
    background: v-bind(getCardBackgroundColor);
}
.card__caption {
    text-align: center;
    color: #fff;
    font-weight: 500;
    line-height: 1.5;
    padding: 12px;
}
.card__wrapper {
    padding: 32px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    background: #fff;
    margin: 2px;
    border-radius: 36px;
}
.card__title {
    font-size: 20px;
    font-weight: 700;
    line-height: 30px;
    text-align: center;
    margin-bottom: 4px;
}
.card__description {
    font-size: 14px;
    line-height: 21px;
    text-align: center;
    margin-bottom: 4px;
}
.card__price_container {
    text-align: center;
    margin-bottom: 4px;
}
.card__price_base {
    font-weight: 300;
    font-size: 16px;
    letter-spacing: -0.02em;
    text-decoration-line: line-through;
    padding-right: 5px;
}
.card__price_real {
    font-weight: 700;
    font-size: 28px;
    line-height: 42px;
    letter-spacing: -0.02em;
}
.card__price_ratio {
    font-weight: 300;
    font-size: 12px;
    line-height: 18px;
}
.card__price_saved {
    display: inline-block;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    background: #FFE500;
    border-radius: 4px;
    padding: 4px 16px;
}
.card__button_container {
    padding: 0 8px 16px;
}
.card__button_container input,
.card__button_container:deep(a),
.card__button_container:deep(span) {
    padding: 12px 16px;
    background-color: var(--primary-color);
    border-radius: 4px;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: #FFFFFF;
    text-align: center;
    text-decoration: none;
    display: block;
}
.card__button_container input {
    width: 100%;
    text-transform: none;
    border: none;
    cursor: pointer;
}
.card__button_container input:hover {
    background-color: var(--primary-color);
    text-shadow: none;
}
.card__button_container:deep(span) {
    background-color: #EBF1F7;
    cursor: default;
    color: #A4B1BE;
    font-weight: 400;
}
.card__about {
    display: flex;
    flex-direction: column;
    margin-bottom: 32px;
}
.card__about div {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    line-height: 21px;
    border-bottom: 1px solid #A4B1BE;
    padding: 8px;
}
.card__about span {
    display: inline-flex;
    align-items: center;
}
.card__about img {
    width: 20px;
}
.card__about a {
    color: var(--primary-color);
}
.card__about > div *:nth-child(2):not(img) {
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    min-width: 70px;
    justify-content: flex-end;
}
.card__more {
    text-align: center;
    font-size: 14px;
    line-height: 21px;
}
.card__more a {
    color: #0B69F5;
}
</style>
